import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { EmailAssessmentComponent } from './components/email-assessment/email-assessment.component';
import { AuthenticationPageGuard } from './shared/guards/authentication.guard';
import { EmailassessmenteResolver } from './shared/guards/emailassessment.resolver';
import { AuthCallbackComponent } from './components/auth-callback/auth-callback.component';
import { SelectivePreloadingStrategy } from './core/services/selective-preloading.strategy';

const routes: Routes = [
  {
    path: '',// re-route
    loadChildren: () =>
      // import('./components/login/login.module').then((m) => m.LoginModule),
      import('./components/adb2c-login/adb2c-login.module').then((m) => m.Adb2cLoginModule),
  },
  {
    //path: 'refresh-token',
    path: 'adb2c',// hotfix
    loadChildren: () =>
      import('./components/adb2c-refresh-token/adb2c-refresh-token.module').then((m) => m.Adb2cRefreshTokenModule),
  },
  {
    path: 'dashboard',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/dashboard/dashboard.module').then(
        (m) => m.DashboardModule
      ),
  },
  {
    path: 'dashboard/:helperIndex',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/dashboard/dashboard.module').then(
        (m) => m.DashboardModule
      ),
  },
  {
    path: 'insights',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/insights/insights.module').then(
        (m) => m.InsightsModule
      ),
  },
  {
    path: 'insights/:dashboardId',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/insights/insights.module').then(
        (m) => m.InsightsModule
      ),
  },
  {
    path: 'settings',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/settings/settings.module').then(
        (m) => m.SettingsModule
      ),
  },
  {
    path: 'questionnaire',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/questionnaire/questionnaire.module').then(
        (m) => m.QuestionnaireModule
      ),
  },
  {
    path: 'people',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/people/people.module').then((m) => m.PeopleModule),
  },

  {
    path: 'people/details',
    canActivate: [AuthenticationPageGuard],
    // Not preloaded (PCIS-11524): this module pulls in the powerbi-client SDK via
    // AdditionalReportsComponent, so blanket-preloading it into every tab (including
    // ones that never open a report) inflates background tabs' memory footprint.
    // Still loads normally on-demand the instant this route is actually navigated to.
    data: { preload: false },
    loadChildren: () =>
      import('./components/people-details/people-details.module').then(
        (m) => m.PeopleDetailsModule
      ),
  },
  {
    path: 'people/details/activesharing',
    canActivate: [AuthenticationPageGuard],
    data: { preload: false },
    loadChildren: () =>
      import('./components/people-details/people-details.module').then(
        (m) => m.PeopleDetailsModule
      ),
  },
  {
    path: 'helper-view-all',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./common/helper-view/helper-view.module').then(
        (m) => m.HelperViewModule
      ),
  },
  {
    path: 'search-dashboard',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/search-dashboard/search-dashboard.module').then(
        (m) => m.SearchDashboardModule
      ),
  },
  {
    path: 'people/add',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/add-people/add-people.module').then(
        (m) => m.AddPeopleModule
      ),
  },
  {
    path: 'strengths-matrics-all',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import(
        './components/strengths-metrics-all/strengths-metrics-all.module'
      ).then((m) => m.StrengthsMetricsAllModule),
  },
  {
    path: 'needs-metrics-all',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/needs-metrics-all/needs-metrics-all.module').then(
        (m) => m.NeedsMetricsAllModule
      ),
  },
  {
    path: 'notifications-all',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/notifications-all/notifications-all.module').then(
        (m) => m.NotificationsAllModule
      ),
  },
  {
    path: 'search-list/:searchText',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/search-list/search-list.module').then(
        (m) => m.SearchListModule
      ),
  },
  {
    path: 'E-ass',
    component: EmailAssessmentComponent,
    resolve: {
      emailassessmentData: EmailassessmenteResolver,
    }
  },
  {
    path: 'email-assessment',
    redirectTo: 'E-ass',
    pathMatch: 'full'
  },
  {
    path: 'reviewer',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/reviewer/reviewer.module').then(
        (m) => m.ReviewerModule
      ),
  },
  {
    path: 'settings/:helperIndex',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/settings/settings.module').then(
        (m) => m.SettingsModule
      ),
  },
  {
    path: 'profile',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/user-profile/user-profile.module').then(
        (m) => m.UserProfileModule
      ),
  },
  {
    path: 'helper-all',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/helper-view-all/helper-view-all.module').then(
        (m) => m.HelperViewAllModule
      ),
  },
  {
    path: 'advanced-search-list',
    canActivate: [AuthenticationPageGuard],
    loadChildren: () =>
      import('./components/advanced-search-list/advanced-search-list.module').then(
        (m) => m.AdvancedSearchListModule
      ),
  },
  {
    path: 'auth/callback',
    component: AuthCallbackComponent,
    data: { title: 'Authentication Callback' }
  },
  {
    path: 'auth/okta',
    loadChildren: () =>
      import('./components/okta-auth/okta-auth.module').then(
        (m) => m.OktaAuthModule
      ),
  },
  {
    path: 'auth/callback',
    loadChildren: () =>
      import('./components/auth-callback/auth-callback.module').then(
        (m) => m.AuthCallbackModule
      ),
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: SelectivePreloadingStrategy })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
